import useSwr from 'swr';
import toast from 'react-hot-toast';

import { PollStatus } from '@sdk/API';
import { listPublicPolls } from '@sdk/managers/PollsManager';

import PollList, { PollListProps } from '@components/elements/PollList';
import { formatPollData, FormattedPoll } from '@utils/Poll';
import { useEffect } from 'react';
import { useAuth } from '@hooks/Auth';

const POLL_FIELDS = [
  {
    items: [
      'companyID',
      { company: ['name'] },
      'id',
      'title',
      'description',
      'status',
      'owner',
      'startTime',
      'endTime',
      'password',
      'private',
      {
        image: ['key', 'bucket', 'region'],
      },
      'pledgePeriod1StartTime',
      'pledgePeriod1EndTime',
      'video',
      'createdAt',
      {
        options: ['id', 'description'],
      },
      { voteResults: [{ items: ['count'] }] },
    ],
  },
  'nextToken',
]

const countTotalVote = (data: any[]) => {
  let total = 0;

  data.forEach((d: any) => {
    total += d.count;
  });

  return total;
};

const getHotPollData = async (
  _query: string,
  companyId?: string,
  grc?: string,
  division?: string
) => {
  const now = new Date();

  let companyFilters = companyId
    ? {
        or: [
          { companyID: { eq: companyId } },
          { companyID: { attributeExists: false } },
        ],
      }
    : { companyID: { attributeExists: false } };

  let locationFilters =
    grc && division
      ? {
          and: [
            { or: [{ grc: { attributeExists: false } }, { grc: { eq: grc } }] },
            {
              or: [
                { division: { attributeExists: false } },
                { division: { eq: division } },
              ],
            },
          ],
        }
      : {
          and: [
            { grc: { attributeExists: false } },
            { division: { attributeExists: false } },
          ],
        };
        const filters = {
          filter: {
            status: { ne: PollStatus.CLOSED },
            ...companyFilters,
            ...locationFilters,
            endTime: {
              ge: now.toISOString(),
            },
            startTime: {
              le: now.toISOString(),
            },
          },
        }
        let polls: any[] = [];
        let nextToken;
        do {
          const res: any = await listPublicPolls(
            POLL_FIELDS,
            filters,
            0,
            nextToken
          )
          if (res.data?.result.items)
            polls = polls.concat(res.data.result.items);
          nextToken = res.data?.result.nextToken;
        } while (nextToken);

        do {
          const res: any = await listPublicPolls(
            POLL_FIELDS,
            filters,
            2,
            nextToken
          )
          if (res.data?.result.items)
            polls = polls.concat(res.data.result.items);
          nextToken = res.data?.result.nextToken;
        } while (nextToken);

  let formattedPolls = (await Promise.all(
    polls.map(async (item: any) => {
      const data = {
        ...item,
        votes: countTotalVote(item.voteResults.items),
      };
      return formatPollData(data);
    })
  )) as FormattedPoll[];

  formattedPolls = formattedPolls.filter((poll) => {
    if (poll.status === 'CLOSED') {
      return false;
    }

    return true;
  });

  formattedPolls.sort((a: FormattedPoll, b: FormattedPoll) => {
    return (
      (b.voteCount || 0) - (a.voteCount || 0) ||
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  });

  return formattedPolls;
};

interface HotPollsProps extends Omit<PollListProps, 'data' | 'loading'> {
  onSelectPoll: (poll: FormattedPoll) => any;
}

const HotPolls: React.FC<HotPollsProps> = ({
  onSelectPoll,
  ...pollListProps
}) => {
  const { company, dbUser } = useAuth();
  const { data, error } = useSwr<FormattedPoll[]>(
    [
      '/poll/hot',
      company?.companyID,
      dbUser?.postalInfo?.grc,
      dbUser?.postalInfo?.division,
    ],
    getHotPollData
  );

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  return (
    <PollList
      data={data}
      loading={!data}
      onSelect={onSelectPoll}
      {...pollListProps}
    />
  );
};

export default HotPolls;
